"use client"

import React from "react"
import { Button, Text, InlineStack, IconSource } from "@shopify/polaris"
import { XIcon } from "@shopify/polaris-icons"

interface CustomModalProps {
  open: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  size?: "extra-small" | "small" | "medium" | "large" | "full"
  primaryAction?: {
    content: string
    onAction: () => void
    loading?: boolean
    disabled?: boolean
  }
  secondaryActions?: Array<{
    content: string
    onAction: () => void
    icon?: IconSource
    loading?: boolean
    disabled?: boolean
  }>
}

export function CustomModal({
  open,
  onClose,
  title,
  children,
  size = "medium",
  primaryAction,
  secondaryActions = []
}: CustomModalProps) {
  if (!open) return null

  const getModalWidth = () => {
    switch (size) {
      case "extra-small":
        return "400px"
      case "small":
        return "600px"
      case "large":
        return "1400px"
      case "full":
        return "100vw"
      case "medium":
        return "800px"
      default:
        return "1400px"
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: size === "full" ? "0" : "20px"
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose()
        }
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: size === "full" ? "0" : "8px",
          width: size === "full" ? "100vw" : "100%",
          maxWidth: getModalWidth(),
          height: size === "full" ? "100vh" : "auto",
          maxHeight: size === "full" ? "100vh" : "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)"
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid #e1e3e5",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#f6f6f7"
          }}
        >
          <Text variant="headingXl" as="h1" fontWeight="bold">
            {title}
          </Text>
          <Button
            icon={XIcon}
            onClick={onClose}
            accessibilityLabel="Close modal"
            variant="tertiary"
            size="slim"
          />
        </div>

        {/* Content */}
        <div
          style={{
            padding: "24px",
            flex: "1",
            overflowY: "auto",
            display: "block"
          }}
        >
          {children}
        </div>

        {/* Footer */}
        {(primaryAction || secondaryActions.length > 0) && (
          <div
            style={{
              padding: "16px 24px",
              borderTop: "1px solid #e1e3e5",
              backgroundColor: "#f6f6f7"
            }}
          >
            <InlineStack align="end" gap="200">
              {secondaryActions.map((action, index) => (
                <Button
                  key={index}
                  onClick={action.onAction}
                  variant="tertiary"
                  icon={action.icon}
                  loading={action.loading}
                  disabled={action.disabled}
                >
                  {action.content}
                </Button>
              ))}
              {primaryAction && (
                <Button
                  onClick={primaryAction.onAction}
                  loading={primaryAction.loading}
                  disabled={primaryAction.disabled}
                  variant="primary"
                >
                  {primaryAction.content}
                </Button>
              )}
            </InlineStack>
          </div>
        )}
      </div>
    </div>
  )
}
